import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useRenameDistrict } from "@/api/districts/mutations";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { getDrfFieldErrors } from "@/utils/errors";

export type RenameTarget = { id: number; name: string };

interface RenameDistrictDialogProps {
	target: RenameTarget | null;
	onOpenChange: (open: boolean) => void;
}

export function RenameDistrictDialog({
	target,
	onOpenChange,
}: RenameDistrictDialogProps) {
	const { t } = useTranslation();
	const [name, setName] = useState(target?.name ?? "");
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
	const rename = useRenameDistrict(target?.id ?? 0);
	const open = target !== null;

	useEffect(() => {
		if (target) {
			setName(target.name ?? "");
			setFieldErrors({});
		}
	}, [target]);

	function handleSave() {
		if (!name.trim() || !target) return;
		setFieldErrors({});
		rename.mutate(
			{ name: name.trim() },
			{
				onSuccess: () => {
					toast.success(t("districts.renamed"));
					onOpenChange(false);
				},
				onError: (error) => {
					setFieldErrors(getDrfFieldErrors(error));
					toast.error(t("districts.renameError"));
				},
			},
		);
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{t("districts.renameTitle")}</DialogTitle>
				</DialogHeader>
				<div className="flex flex-col gap-1.5">
					<Input
						value={name}
						onChange={(e) => setName(e.target.value)}
						onKeyDown={(e) => e.key === "Enter" && handleSave()}
						placeholder={t("districts.namePlaceholder")}
						autoFocus
						aria-invalid={!!fieldErrors.name}
					/>
					{fieldErrors.name && (
						<p className="text-destructive text-sm" role="alert">
							{fieldErrors.name}
						</p>
					)}
				</div>
				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={rename.isPending}
					>
						{t("districts.cancel")}
					</Button>
					<Button
						onClick={handleSave}
						disabled={!name.trim() || rename.isPending}
					>
						{rename.isPending ? t("districts.saving") : t("districts.save")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
